/** Default homepage CMS content — Harvyq */
export const HOMEPAGE_DEFAULTS = {
  brand: {
    productName: 'Harvyq',
    parentCompany: 'Boldcomp Solutions',
    tagline: 'Business Management Software Built for Kenya',
    logoUrl: '',
    logoAlt: 'Harvyq'
  },
  hero: {
    badge: 'Business Management Software Built for Kenya',
    headline: 'Run Your Entire Business From One Place',
    subheadline: 'Manage sales, stock, customers, suppliers, expenses and business performance without juggling notebooks, spreadsheets and disconnected systems.',
    primaryCta: 'Start Free for 14 Days',
    primaryCtaLink: '/register',
    secondaryCta: 'Book a Free Demo',
    secondaryCtaLink: '#demo',
    microcopy: 'No credit card required • Setup assistance • Local support'
  },
  trust: {
    headline: 'Built with Kenyan businesses.',
    logos: ['Retail', 'Hardware', 'Wholesale', 'Agrovet', 'Electronics']
  },
  problems: {
    sectionTitle: "Running a growing business shouldn't feel this complicated.",
    items: [
      { text: "Not sure what's actually in stock?" },
      { text: "Customers owe you money but balances are difficult to track?" },
      { text: "Can't see what your shop is selling when you're away?" }
    ],
    solutionTransitionTitle: "Harvyq brings everything together."
  },
  demo: {
    enabled: true,
    sectionTitle: "See what's happening in your business — instantly.",
    sectionSubtitle: "Real-time visibility into today's sales, inventory, outstanding balances, and branch performance.",
    videoUrl: '' 
  },
  capabilities: {
    sectionTitle: "Everything you need to run your business.",
    items: [
      { icon: '🛒', title: 'Process Sales Quickly', description: 'Fast Point of Sale (POS) system that handles walk-in customers seamlessly and records every transaction.' },
      { icon: '📦', title: "Know Exactly What's In Stock", description: "Track stock movements, identify low-stock products and know what is selling without manually counting everything." },
      { icon: '👥', title: 'Track Customer Balances', description: 'Record credit sales instantly. Know exactly who owes you money, how much, and track their payment history.' },
      { icon: '💰', title: 'Manage Suppliers & Purchases', description: 'Keep a clear record of your suppliers, track incoming stock, and manage your purchasing costs accurately.' },
      { icon: '📊', title: 'See Business Reports', description: 'Get instant insights into your daily sales, expenses, profits, and overall business health from anywhere.' },
      { icon: '🏢', title: 'Control Multiple Branches', description: 'Expand your business confidently. Monitor performance and stock across all your locations from a single dashboard.' }
    ]
  },
  industries: {
    sectionTitle: "Harvyq works the way your business works.",
    items: [
      { icon: '🔨', title: 'Hardware Stores', link: '/hardware', highlight: false },
      { icon: '📦', title: 'Wholesalers & Distributors', link: '/wholesale', highlight: false },
      { icon: '🛒', title: 'Retail Shops', link: '/retail', highlight: false },
      { icon: '🏪', title: 'Minimarts', link: '/minimart', highlight: false },
      { icon: '💻', title: 'Electronics Shops', link: '/electronics', highlight: false },
      { icon: '⚙️', title: 'Spare Parts', link: '/spare-parts', highlight: false },
      { icon: '🌱', title: 'Agrovets', link: '/agrovet', highlight: false },
      { icon: '🚀', title: 'Other Growing Businesses', link: '/register', highlight: true }
    ]
  },
  howItWorks: {
    sectionTitle: "Get your business running on Harvyq.",
    steps: [
      { number: '1', title: 'Create your account', description: 'Sign up in seconds. No credit card required to start your free trial.' },
      { number: '2', title: 'Add your products', description: 'Import your stock and customers easily, with our onboarding assistance.' },
      { number: '3', title: 'Start selling', description: 'Process sales, track expenses, and manage operations immediately.' },
      { number: '4', title: 'Monitor everything', description: 'Watch your business performance in real-time from your dashboard.' }
    ]
  },
  localization: {
    sectionTitle: "Built for doing business in Kenya.",
    features: [
      "Kenyan Shillings (KSh) Native",
      "Local Customer Support",
      "Mobile-friendly Access",
      "Multi-branch Operations"
    ]
  },
  caseStudy: {
    quote: '"Harvyq completely changed how we run our shop. We used to lose track of stock and credit sales. Now, I can see exactly what\'s happening even when I\'m not at the counter."',
    authorName: 'Martha Kachelawa',
    authorRole: 'Owner, Boldzen Hardware, Nairobi'
  },
  pricing: {
    sectionTitle: "Simple pricing for growing businesses.",
    plans: [
      {
        name: 'Duka',
        desc: 'Designed for small shops starting to digitize.',
        priceMonthly: '1,500',
        priceAnnually: '15,000',
        cta: 'Start Free',
        features: ['POS System', 'Basic Inventory', 'Basic Reporting', '1 User'],
        popular: false
      },
      {
        name: 'Business',
        desc: 'For established SMEs needing stronger capabilities.',
        priceMonthly: '3,500',
        priceAnnually: '35,000',
        cta: 'Start Free',
        features: ['Everything in Duka', 'Customer & Debt Tracking', 'Supplier & Purchase Management', 'Expense Tracking', 'Advanced Reports'],
        popular: true
      },
      {
        name: 'Growth',
        desc: 'For businesses requiring multi-branch management.',
        priceMonthly: '6,500',
        priceAnnually: '65,000',
        cta: 'Start Free',
        features: ['Everything in Business', 'Multi-Branch Management', 'More Users', 'Advanced Analytics', 'Priority Support'],
        popular: false
      },
      {
        name: 'Enterprise',
        desc: 'For chains, distributors and organizations.',
        priceMonthly: 'Custom',
        priceAnnually: 'Custom',
        cta: 'Talk to Sales',
        features: ['Custom Implementation', 'Unlimited Branches & Users', 'API Integrations', 'Dedicated Account Manager', 'Custom Onboarding'],
        popular: false
      }
    ]
  },
  faq: {
    sectionTitle: 'Frequently Asked Questions',
    items: [
      { q: "What is Harvyq?", a: "Harvyq is a complete business management system designed for Kenyan SMEs. It brings your sales, inventory, customers, suppliers, and multi-branch operations into one easy-to-use platform." },
      { q: "What types of businesses can use Harvyq?", a: "Harvyq is built for growing businesses including hardware stores, wholesalers, retail shops, minimarts, electronics shops, agrovets, and auto spare parts dealers." },
      { q: "Do I need accounting knowledge to use Harvyq?", a: "Not at all. We designed Harvyq to be incredibly simple. If you can use a smartphone, you can use Harvyq to manage your business." },
      { q: "Can I manage multiple branches?", a: "Yes, our Growth and Enterprise plans allow you to track stock, sales, and staff across multiple locations from a single dashboard." },
      { q: "Can I track customers who owe me money?", a: "Yes. Harvyq has built-in credit tracking so you always know exactly who owes you money, how much, and their payment history." },
      { q: "Can Harvyq manage inventory?", a: "Absolutely. Harvyq tracks stock movements in real-time, alerts you when products are running low, and helps you manage purchasing from suppliers." },
      { q: "Can I import my existing products?", a: "Yes, you can easily bulk import your products and customers using Excel or CSV files during setup." },
      { q: "Can my employees use Harvyq?", a: "Yes, you can add staff members (cashiers, managers) with specific permissions so they only see what they need to." },
      { q: "Can I access Harvyq from my phone?", a: "Yes, Harvyq is a cloud-based system that works beautifully on laptops, tablets, and smartphones." },
      { q: "Is my business data secure?", a: "Your data is stored on secure, encrypted cloud servers and backed up daily. Only authorized users with your permission can access it." },
      { q: "Do you provide training?", a: "Yes, we provide onboarding assistance to ensure you and your team are comfortable using the system." },
      { q: "What happens after the free trial?", a: "After 14 days, you can choose the plan that best fits your business. You will not be charged automatically since no credit card is required to start." }
    ]
  },
  cta: {
    headline: 'Ready to take control of your business?',
    subtext: 'Start using Harvyq and bring your sales, stock, customers and operations into one place.',
    primaryCta: 'Start Free for 14 Days',
    primaryCtaLink: '/register',
    secondaryCta: 'Book a Free Demo',
    secondaryCtaLink: '#demo',
    microcopy: 'No credit card required.'
  },
  footer: {
    about: 'Business management software built for growing Kenyan businesses.',
    phone: '+254 726 642 205',
    email: 'info@harvyq.com',
    address: 'Nairobi, Kenya',
    copyright: '© 2026 Harvyq by Boldcomp Solutions. All rights reserved.',
    social: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: ''
    }
  }
};

export function deepMergeHomepage(saved = {}) {
  return {
    ...HOMEPAGE_DEFAULTS,
    ...saved,
    brand: { ...HOMEPAGE_DEFAULTS.brand, ...(saved.brand || {}) },
    hero: { ...HOMEPAGE_DEFAULTS.hero, ...(saved.hero || {}) },
    trust: {
      ...HOMEPAGE_DEFAULTS.trust,
      ...(saved.trust || {}),
      logos: saved.trust?.logos?.length ? saved.trust.logos : HOMEPAGE_DEFAULTS.trust.logos
    },
    problems: {
      ...HOMEPAGE_DEFAULTS.problems,
      ...(saved.problems || {}),
      items: saved.problems?.items?.length ? saved.problems.items : HOMEPAGE_DEFAULTS.problems.items
    },
    demo: { ...HOMEPAGE_DEFAULTS.demo, ...(saved.demo || {}) },
    capabilities: {
      ...HOMEPAGE_DEFAULTS.capabilities,
      ...(saved.capabilities || {}),
      items: saved.capabilities?.items?.length ? saved.capabilities.items : HOMEPAGE_DEFAULTS.capabilities.items
    },
    industries: {
      ...HOMEPAGE_DEFAULTS.industries,
      ...(saved.industries || {}),
      items: saved.industries?.items?.length ? saved.industries.items : HOMEPAGE_DEFAULTS.industries.items
    },
    howItWorks: {
      ...HOMEPAGE_DEFAULTS.howItWorks,
      ...(saved.howItWorks || {}),
      steps: saved.howItWorks?.steps?.length ? saved.howItWorks.steps : HOMEPAGE_DEFAULTS.howItWorks.steps
    },
    localization: {
      ...HOMEPAGE_DEFAULTS.localization,
      ...(saved.localization || {}),
      features: saved.localization?.features?.length ? saved.localization.features : HOMEPAGE_DEFAULTS.localization.features
    },
    caseStudy: { ...HOMEPAGE_DEFAULTS.caseStudy, ...(saved.caseStudy || {}) },
    pricing: {
      ...HOMEPAGE_DEFAULTS.pricing,
      ...(saved.pricing || {}),
      plans: saved.pricing?.plans?.length ? saved.pricing.plans : HOMEPAGE_DEFAULTS.pricing.plans
    },
    faq: {
      ...HOMEPAGE_DEFAULTS.faq,
      ...(saved.faq || {}),
      items: saved.faq?.items?.length ? saved.faq.items : HOMEPAGE_DEFAULTS.faq.items
    },
    cta: { ...HOMEPAGE_DEFAULTS.cta, ...(saved.cta || {}) },
    footer: {
      ...HOMEPAGE_DEFAULTS.footer,
      ...(saved.footer || {}),
      social: { ...HOMEPAGE_DEFAULTS.footer.social, ...(saved.footer?.social || {}) }
    }
  };
}

/** Convert YouTube / Vimeo / direct URL to an embeddable iframe src */
export function toEmbedUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  const yt =
    trimmed.match(/youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)([\w-]{11})/) ||
    trimmed.match(/youtu\.be\/([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;

  const vimeo = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;

  const drive = trimmed.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
  if (drive) return `https://drive.google.com/file/d/${drive[1]}/preview`;

  return trimmed;
}

export function isDirectVideoFile(url) {
  return /\.(mp4|webm|ogg)(\?.*)?$/i.test((url || '').trim());
}

/** Demo video saved by admin: `videoUrl`, or the first `videos[].url` from older saves */
export function getDemoVideoUrl(demo) {
  if (!demo) return '';
  if (demo.videoUrl && demo.videoUrl.trim()) return demo.videoUrl.trim();
  const fromList = (demo.videos || []).find((v) => v?.url && v.url.trim());
  return fromList ? fromList.url.trim() : '';
}

export function resolveMediaUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return path.startsWith('/') ? path : `/${path}`;
}
